import React from "react";
import { styled } from "@linaria/react";
import createGlobalState from "@ui/lib/createGlobalState";
import t from "../../../../../../i18n";
import Section from "@ui/Section";
import TableCard, { HeaderRight } from "@ui/Table/Card";
import * as io from "io-ts";
import { literalUnion, long, nullable } from "diagnostics-lib/io";
import { isNonEmptyArray } from "diagnostics-lib/array";
import type { SortableValue } from "diagnostics-lib/comparator";
import { comparatorBy } from "diagnostics-lib/comparator";
import FilterSelects, { filterData, NONE_LABEL } from "@ui/Filter";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import InlineMenu from "@ui/InlineMenu";
import Option, { OptionGroup } from "@ui/Option";
import Link from "@ui/Link";
import type { Column, EditableQuantity } from "./useMetadata";
import useMetadata, {
  columns,
  computeRowKey,
  getLabel,
  getRequiredVisibleColumns,
} from "./useMetadata";
import useSortedData from "../../../../../../lib/hooks/useSortedData";
import Table from "@ui/Table";
import createIOSortType from "../../../../../../lib/createIOSortType";
import Modal from "@ui/Modal";
import { useModals } from "@ui/lib/hooks/useModal";
import Button from "@ui/Button";
import {
  useQuantities,
  useQuantityIndex,
  type Quantity,
} from "../../../../../../api/useQuantities";
import { color, fonts } from "@ui";
import { getQuantityTypeLabel } from "../../utils/getQuantityType";
import download from "downloadjs";
import useQuery from "../../../../../../api/useQuery";
import { useDelete, useGet, usePost } from "../../../../../../api/useCall";
import { useSetups } from "../../../../../../api/useSetups";
import { useCanUserDoOnProject } from "../../../../../canUserDo";
import { hasThreshold } from "../../utils/getThresholdLabel";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { NEW_FORMULA_TMP_ID } from "../../utils/constants";
import { AddCircleLine, DownloadLine, More2Fill, UploadLine } from "@ui/IconV2/react";
import { generateExportFileName } from "diagnostics-lib/string";
import BlankStateV2 from "@ui/BlankStateV2";
import { colors } from "@ui/Theme";
import Menu from "@ui/Menu";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { TableCardWithoutBorder } from "@ui/Table/_styles/TableCardWithoutBorder";
import { LoadedFileName } from "../../../LoadedFileName";
import { MAX_QUANTITY_COUNT } from "diagnostics-domain/constants";
import type { Sensor } from "../../modalForm/CreateSensorQuantityModal";
import ImportQuantitiesModal from "../../modalForm/ImportQuantitiesForm";
import ConfirmDelete from "../../modalForm/ConfirmDelete";
import CreateSensorQuantityModal from "../../modalForm/CreateSensorQuantityModal";
import { TableWithBorder } from "@ui/Table/_styles/TableWithBorder";
import { useOutletContext } from "react-router";
import type { QuantityLayoutOutletContext } from "../..";
import useToasts from "@ui/Toast/useToasts";
import { useDashboards } from "../../../../Dashboards/hook/api/dashboard";
import {
  getCheckQuantityUsedInSetupsOrFormulas,
  getQuantityUsedInDashboard,
} from "diagnostics-domain/quantity/usage";
import { useFormulaVersions } from "../../../../../../api/quantity/formula-version";
import useWhyDidYouUpdate from "../../../../../../lib/hooks/useWhyDidYouUpdate";
const StyledTableCard = styled(TableCard)`
  ${HeaderRight} {
    column-gap: 8px;
  }
`;

const StyledMenu = styled(Menu)`
  font: ${fonts.body2};
`;

const StyledInlineMenu = styled(InlineMenu)`
  display: flex;
  align-items: flex-end;
  font: ${fonts.body2};
  @media (max-width: 650px) {
    margin-right: auto;
    margin-left: 10px;
  }
`;
const StyledBlankStateV2 = styled(BlankStateV2)`
  margin-top: 24px;
  background-color: ${colors.hydrogen};
  border: 1px solid ${colors.titanium200};
  border-radius: 4px;
`;

const FILTER_KEYS = ["type", "unit"] as const;
const FilterKey = literalUnion(FILTER_KEYS);
type FilterKey = (typeof FILTER_KEYS)[number];

const FilteredValuesByFilterType = io.strict({
  type: io.array(io.string),
  unit: io.array(nullable(io.string)),
  threshold: io.array(io.string),
  usage: io.array(io.union([io.literal("FORMULA"), io.literal("SETUP"), io.literal("DASHBOARD")])),
});
type FilteredValuesByFilterType = io.TypeOf<typeof FilteredValuesByFilterType>;

const DEFAULT_FILTER_VALUES: FilteredValuesByFilterType = {
  type: [],
  unit: [],
  threshold: [],
  usage: [],
};

const useFilteredValuesByFilterType = createGlobalState({
  storage: "session",
  key: "quantitiesTableFilteredValue",
  type: FilteredValuesByFilterType,
  defaultValue: DEFAULT_FILTER_VALUES,
  scoped: true,
});

const useQuantitiesSearch = createGlobalState({
  storage: "session",
  key: "quantitiesSearch",
  type: io.string,
  defaultValue: "",
  scoped: true,
});

function getQuantityUsageLabel(quantityType: "FORMULA" | "SETUP" | "DASHBOARD") {
  switch (quantityType) {
    case "FORMULA":
      return t("commons.quantities.selectFilter.type.formula");
    case "SETUP":
      return t("commons.quantities.selectFilter.type.setup");
    case "DASHBOARD":
      return t("commons.quantities.selectFilter.type.dashboard");
    default:
      throw new Error(quantityType satisfies never);
  }
}

const FILTER_METADATA = {
  type: {
    title: t("commons.quantities.selectFilter.type"),
    getOptions: (quantity: EditableQuantity) => ({
      label: getQuantityTypeLabel(quantity.type),
      value: quantity.type.toString(),
    }),
  },
  unit: {
    title: t("commons.quantities.selectFilter.unit"),
    getOptions: (quantity: EditableQuantity) => ({
      label: quantity.unit ?? NONE_LABEL,
      value: quantity.unit ?? null,
    }),
    withNone: true as const,
  },
  threshold: {
    title: t("commons.quantities.selectFilter.threshold"),
    getOptions: (quantity: EditableQuantity) => ({
      label: hasThreshold(quantity),
      value: hasThreshold(quantity),
    }),
    withNone: true as const,
  },
  usage: {
    title: "Usage",
    getOptions: (quantity: EditableQuantity) =>
      quantity.usages === undefined
        ? null
        : quantity.usages.map(v => ({
            label: getQuantityUsageLabel(v),
            value: v,
          })),
    withNone: false as const,
  },
};

const useSort = createGlobalState({
  storage: "session",
  key: "quantitySort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "name",
    direction: "asc",
  },
  scoped: true,
});

const sortSelector: { [key in Column]: (s: EditableQuantity) => SortableValue | undefined | null } =
  {
    usage: _ => null,
    name: q => q.name,
    hidden: q => (q.hidden ? 1 : 0),
    description: q => q.description,
    unit: q => q.unit,
    formula: q => q.formula,
    sensorTag: q => q.sensor?.tag,
    min: q => q.minMetrology,
    max: q => q.maxMetrology,
    maxStd: q => q.maxStdMetrology,
    equipments: _ => null,
    system: q => q.systemId,
    equipmentType: q => q.equipmentTypeId,
    actions: _ => null,
  };

const FormSignature = io.partial({
  name: io.string,
  description: io.string, // string
  unit: io.string, // non-empty string
  formula: long(io.string),
  min: io.string,
  max: io.string,
  maxStd: io.string,
  system: io.string,
  equipmentType: io.string,
  equipments: io.array(io.string),
});

export type FormSignature = io.TypeOf<typeof FormSignature>;

const filterQuantities = filterData<EditableQuantity, FilterKey, typeof FILTER_METADATA>(
  FILTER_METADATA,
);

export const Quantities = () => {
  const { addToast } = useToasts();
  const { project, isQuantityPanelClosed, onEditQuantity, onOpenCreateQuantityPanel } =
    useOutletContext<QuantityLayoutOutletContext>();
  const setups = useSetups(project.id);
  const hasSetup = setups.length > 0;
  const canUserDoAction = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const canUserViewIntermediateCalculations = useCanUserDoOnProject("PROJECT_SETUP", project.id);
  const quantities = useQuantities(project.id);
  const formulaVersions = useFormulaVersions(project.id);
  useWhyDidYouUpdate("formulaVersions", { formulaVersions });
  const quantityIndex = useQuantityIndex(project.id);
  const sensors = useQuery("/projects/:projectId(uuid)/sensors", {
    routeParams: { projectId: project.id },
  });
  const [newFormula, setNewFormula] = React.useState<EditableQuantity>();

  const removeNewFormula = React.useCallback(() => setNewFormula(undefined), []);
  const deleteQuantity = useDelete("/projects/:projectId(uuid)/quantities/:quantityId(uuid)");
  const createSensorQuantities = usePost("/projects/:projectId(uuid)/quantities/sensors");

  const [sort, setSort] = useSort({ scope: project.id });
  // Delete modal: inline delete of a quantity
  // Create modal: "tags capteur non utilisés" to create new quantities from sensors != "Nouvelle grandeur calculée"
  // Import modal: import quantities from a file
  const { closeModal, openModal, modal } = useModals<"delete" | "create" | "import">();
  const [selectedQuantity, setSelectedQuantity] = React.useState<Quantity>();
  const [search, setSearch] = useQuantitiesSearch({ scope: project.id });

  const [filteredValues, setFilteredValues] = useFilteredValuesByFilterType({ scope: project.id });
  const [menuOpened, setMenuOpened] = React.useState(false);

  const availableSensors: Sensor[] = React.useMemo(
    () => sensors.body.filter(s => !quantities.some(q => q.sensor?.id === s.id)),
    [quantities, sensors],
  );

  const handleDelete = React.useCallback(async () => {
    if (!selectedQuantity) throw new Error("Should not happen");

    // in case we added a new formula but we didnt push it to the backend yet
    if (selectedQuantity.id === NEW_FORMULA_TMP_ID) {
      setNewFormula(undefined);
    } else {
      try {
        await deleteQuantity({
          routeParams: { projectId: project.id, quantityId: selectedQuantity.id },
        });
        addToast("success", t("project.quantity.toast.deleted.success"));
      } catch (error) {
        addToast("error", t("errors.backend_error"));
      }
    }
    return true;
  }, [deleteQuantity, project.id, selectedQuantity, addToast]);

  const openDeleteModal = React.useCallback(
    (quantity: EditableQuantity) => () => {
      setSelectedQuantity(quantityIndex[quantity.id]);
      openModal("delete");
    },
    [openModal, quantityIndex],
  );

  const { blocks } = useDashboards(project.id);
  const formatedQuantities = React.useMemo(
    () =>
      quantities.flatMap(q => {
        if (q.hidden && !canUserViewIntermediateCalculations) return [];

        const usages: ("FORMULA" | "SETUP" | "DASHBOARD")[] = [];
        const quantity = quantityIndex[q.id];
        if (!quantity) return [];

        const { hasImpactOnSetups, isUsedInFormula } = getCheckQuantityUsedInSetupsOrFormulas(
          setups,
          quantity,
        );
        if (isUsedInFormula) usages.push("FORMULA");
        if (hasImpactOnSetups) usages.push("SETUP");
        const isUsedInDahshboard = getQuantityUsedInDashboard(
          q.name,
          blocks.filter(f => f.kind === "PLOT"),
          blocks.filter(f => f.kind === "DIAGRAM"),
        );
        if (isUsedInDahshboard) usages.push("DASHBOARD");

        return [{ ...q, usages }];
      }),
    [quantities, blocks, canUserViewIntermediateCalculations, quantityIndex, setups],
  );

  const { metadata, visibleColumns, setVisibleColumns, datas, setEditedDataKey } = useMetadata({
    removeNewFormula,
    projectId: project.id,
    quantities: newFormula ? [...formatedQuantities, newFormula] : formatedQuantities,
    formulaVersions,
    quantityIndex,
    openDeleteModal,
    onEditQuantity,
    isQuantityPanelClosed,
  });

  const filteredQuantities = React.useMemo(
    () => filterQuantities(datas, filteredValues),
    [datas, filteredValues],
  );

  const filterPredicate = React.useCallback((d: EditableQuantity) => d.name, []);

  const [_searchedQuantities, searchInProgress] = useFilteredDatas(
    filteredQuantities,
    search,
    filterPredicate,
  );

  // if we are creating a new quantity and it is not in the filtered data, it must be shown or the button will appear broken
  const searchedQuantities = React.useMemo(
    () =>
      newFormula && !_searchedQuantities.some(q => q.id === NEW_FORMULA_TMP_ID)
        ? [..._searchedQuantities, newFormula]
        : _searchedQuantities,
    [_searchedQuantities, newFormula],
  );

  const sortedQuantities = useSortedData(
    searchedQuantities,
    sortSelector[sort.column],
    sort.direction,
  );

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  const headerLeft = React.useMemo(
    () => (
      <>
        <SearchInputV2
          data-cy="search-project"
          placeholder={t("commons.placeholder.search")}
          onChange={handleSearchChange}
          value={search}
        />
        <FilterSelects
          data={formatedQuantities}
          metadata={FILTER_METADATA}
          filterValues={filteredValues}
          setFilterValues={setFilteredValues}
          defaultFilterValues={DEFAULT_FILTER_VALUES}
        />
      </>
    ),
    [formatedQuantities, handleSearchChange, search, filteredValues, setFilteredValues],
  );

  const hasQuantitiesExportableElements = quantities.length > 0;
  const exportQuantities = useGet("/projects/:projectId(uuid)/quantities/export");
  const handleExport = React.useCallback(async () => {
    const res = await exportQuantities({ routeParams: { projectId: project.id } });
    download(
      res.body as unknown as Blob,
      generateExportFileName({
        filename: "quantities",
        projectName: project.name,
        extension: "csv",
      }),
    );
  }, [exportQuantities, project.name, project.id]);

  const tooManyQuantities =
    quantities.length >= MAX_QUANTITY_COUNT ? t("projects.quantities.too_many") : undefined;

  const headerRight = React.useMemo(() => {
    const showMore2FillOptions = canUserDoAction && (!hasSetup || hasQuantitiesExportableElements);

    const addButtonDisabled = Boolean(tooManyQuantities) || !isQuantityPanelClosed;

    return (
      <>
        <StyledInlineMenu
          anchor="right"
          opened={menuOpened}
          setOpened={setMenuOpened}
          caret
          overlay={columns
            .filter(column => {
              if (!canUserDoAction && column === "actions") return false;
              if (!canUserViewIntermediateCalculations && column === "hidden") return false;
              return true;
            })
            .map(column => (
              <Option
                key={column}
                checkbox
                disabled={getRequiredVisibleColumns(canUserDoAction).includes(column)}
                selected={visibleColumns.includes(column)}
                onClick={() =>
                  setVisibleColumns(
                    visibleColumns.includes(column)
                      ? visibleColumns.filter(c => c !== column)
                      : [...visibleColumns, column],
                  )
                }
              >
                {getLabel(column)}
              </Option>
            ))}
        >
          <Link>{t("commons.select.manage_columns")}</Link>
        </StyledInlineMenu>
        {canUserDoAction && (
          <>
            {isNonEmptyArray(availableSensors) && (
              <Button
                disabled={addButtonDisabled}
                title={
                  addButtonDisabled
                    ? t("projects.actions.sensor_tag.disabled.side_panel_open")
                    : tooManyQuantities
                }
                variant="primary"
                onClick={() => openModal("create")}
                leftIcon={<AddCircleLine />}
              >
                {t("project.quantities.button.newQuantities", {
                  newSensorCount: availableSensors.length,
                })}
              </Button>
            )}
            <Button
              disabled={addButtonDisabled}
              title={
                addButtonDisabled
                  ? t("projects.actions.new_calculated_quantity.disabled.side_panel_open")
                  : tooManyQuantities
              }
              variant="primary"
              onClick={onOpenCreateQuantityPanel} // Use the new handler
              leftIcon={<AddCircleLine />}
            >
              {t("project.quantities.formula.add")}
            </Button>
          </>
        )}
        {showMore2FillOptions ? (
          <StyledMenu
            arrowWidth={0}
            // gap={16}
            placement="bottom-end"
            overlay={
              <>
                <OptionGroup>
                  <Option
                    closeOnClick
                    onClick={() => openModal("import")}
                    disabled={!canUserDoAction}
                  >
                    <>
                      <UploadLine color={color.carbon800} />
                      {t("commons.message_card.replace")}
                    </>
                  </Option>
                  <Option
                    closeOnClick
                    onClick={handleExport}
                    disabled={!canUserDoAction || !hasQuantitiesExportableElements}
                  >
                    <DownloadLine color={color.carbon800} />
                    {t("commons.message_card.export")}
                  </Option>
                </OptionGroup>
              </>
            }
          >
            <ButtonIconV2>
              <More2Fill size="m" />
            </ButtonIconV2>
          </StyledMenu>
        ) : null}
      </>
    );
  }, [
    menuOpened,
    setMenuOpened,
    visibleColumns,
    setVisibleColumns,
    canUserDoAction,
    canUserViewIntermediateCalculations,
    hasSetup,
    openModal,
    hasQuantitiesExportableElements,
    handleExport,
    availableSensors,
    tooManyQuantities,
    isQuantityPanelClosed,
    onOpenCreateQuantityPanel,
  ]);

  const handleCreateSensorQuantities = React.useCallback(
    async (selection: Sensor[]) => {
      await createSensorQuantities({
        body: selection.map(sensor => sensor.id),
        routeParams: { projectId: project.id },
      });
    },
    [createSensorQuantities, project.id],
  );

  if (quantities.length === 0) {
    return (
      <>
        <LoadedFileName fileName={project.quantitiesFileName} />
        <StyledBlankStateV2
          title={t("quantities.blankState.title")}
          subtitle={canUserDoAction ? t("quantities.blankState.subtitle") : null}
          onClick={canUserDoAction ? () => openModal("import") : undefined}
          buttonLabel={
            canUserDoAction ? t("quantities.crossFilter.blankState.button_label") : undefined
          }
          buttonPicto={<UploadLine />}
        />
        <Modal
          title={t("project.quantities.import.title")}
          isOpen={modal === "import"}
          onRequestClose={closeModal}
          size="xl"
        >
          <ImportQuantitiesModal
            onRequestClose={closeModal}
            project={project}
            onSetEditedDataKey={setEditedDataKey}
          />
        </Modal>
      </>
    );
  }

  return (
    <>
      <LoadedFileName fileName={project.quantitiesFileName} />
      <Section id="quantities" title="">
        <StyledTableCard
          title=""
          headerLeft={headerLeft}
          headerRight={headerRight}
          className={TableCardWithoutBorder}
        >
          <Table
            stickyHeader
            loading={searchInProgress}
            datas={sortedQuantities}
            metadata={metadata}
            computeRowKey={computeRowKey}
            sort={sort}
            onSort={setSort}
            className={TableWithBorder}
          />
        </StyledTableCard>
      </Section>

      {/* Modal to confirm deletion */}
      <Modal
        title={t("project.quantities.delete.title")}
        isOpen={modal === "delete"}
        onRequestClose={closeModal}
      >
        {selectedQuantity === undefined ? null : (
          <ConfirmDelete
            projectId={project.id}
            onRequestClose={closeModal}
            quantity={selectedQuantity}
            handleSubmit={handleDelete}
          />
        )}
      </Modal>

      {/* Modal to create quantities from unused sensors */}
      <Modal
        title={t("project.quantities.add.title", {
          newSensorCount: availableSensors.length,
        })}
        isOpen={modal === "create"}
        onRequestClose={closeModal}
        size="xl"
      >
        {isNonEmptyArray(availableSensors) && (
          <CreateSensorQuantityModal
            availableSensors={availableSensors.sort(comparatorBy("tag"))}
            sourceId={project.sourceId}
            onRequestClose={closeModal}
            createQuantities={handleCreateSensorQuantities}
          />
        )}
      </Modal>

      <Modal
        title={t("project.quantities.import.title")}
        isOpen={modal === "import"}
        onRequestClose={closeModal}
        size="xl"
      >
        <ImportQuantitiesModal
          onRequestClose={closeModal}
          project={project}
          onSetEditedDataKey={setEditedDataKey}
        />
      </Modal>
    </>
  );
};
